import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import { useT, useLp } from "../lib/i18n";
import {
  exchangeOAuthCode,
  createCheckoutSession,
  formatError,
  type CheckoutCycle,
} from "../lib/api";
import { saveSession } from "../lib/auth";
import { callbackURL, consumeOAuthState } from "../lib/cognito";
import {
  M1_PLAUSIBLE_EVENTS,
  trackPlausibleEvent,
  trackPlausibleEventBeforeRedirect,
} from "../lib/plausible";
import { attributionProps, getAttribution } from "../lib/attribution";
import { trackLaunchSignupOnce } from "../lib/launch-funnel";

function parseCheckoutCycle(value: string | null): CheckoutCycle | undefined {
  return value === "monthly" || value === "annual" || value === "founding100"
    ? value
    : undefined;
}

/**
 * Cognito Hosted UI redirects here after a federated sign-in (e.g. Google).
 * We validate the CSRF `state`, exchange the authorization code for a
 * TokenSet via the Go API, persist the session, then return the user to
 * wherever they were headed before kicking off the flow.
 *
 * StrictMode runs effects twice in dev, but the authorization code is
 * single-use — the second exchange would fail with `oauth_invalid_grant`.
 * The `didRunRef` guard makes the effect idempotent across re-mounts.
 */
export function OAuthCallback() {
  const t = useT();
  const lp = useLp();
  const tl = t.auth.login;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;

    const oauthError = params.get("error");
    if (oauthError) {
      setError(params.get("error_description") || oauthError);
      return;
    }

    const code = params.get("code");
    const state = params.get("state");
    if (!code) {
      setError(tl.callbackError);
      return;
    }

    const verifier = consumeOAuthState(state);
    if (!verifier) {
      // State mismatch or expired sessionStorage. Treat as a failed sign-in
      // rather than risk binding the response to the wrong tab.
      setError(tl.callbackError);
      return;
    }

    (async () => {
      try {
        const tokens = await exchangeOAuthCode(code, callbackURL(), verifier);
        saveSession(tokens);

        const signupPlan = sessionStorage.getItem("minuta.oauth.signup_plan");
        sessionStorage.removeItem("minuta.oauth.signup_plan");
        if (signupPlan === "free") {
          trackPlausibleEvent(M1_PLAUSIBLE_EVENTS.signupFree, attributionProps());
          trackLaunchSignupOnce("oauth.free");
        }

        // Pro signup chained through OAuth — go straight to Stripe afterwards.
        // Mirrors the email/password login path in AuthModal.
        const next = sessionStorage.getItem("minuta.oauth.next");
        sessionStorage.removeItem("minuta.oauth.next");
        const cycle = parseCheckoutCycle(sessionStorage.getItem("minuta.oauth.checkout_cycle"));
        sessionStorage.removeItem("minuta.oauth.checkout_cycle");
        if (next === "checkout") {
          try {
            const attr = getAttribution();
            const { url } = await createCheckoutSession(tokens.access_token, { cycle, ...attr });
            trackPlausibleEventBeforeRedirect(
              M1_PLAUSIBLE_EVENTS.checkoutStarted,
              () => window.location.assign(url),
              attributionProps(),
            );
            return;
          } catch {
            // Stripe unreachable — fall through to home so the user lands
            // somewhere sensible instead of stuck on this page.
          }
        }
        navigate(lp + "/", { replace: true });
      } catch (err) {
        setError(formatError(err));
      }
    })();
  }, [params, navigate, lp, tl.callbackError]);

  return (
    <AuthShell>
      {error ? (
        <div className="space-y-6 text-center">
          <h1 className="text-xl font-semibold text-ink dark:text-paper">{tl.heading}</h1>
          <p className="text-sm text-rec">{error}</p>
          <Link
            to={lp + "/?auth=login"}
            replace
            className="inline-flex items-center justify-center rounded-full bg-graphite text-paper hover:bg-graphite-muted transition-colors px-5 py-3 text-sm font-medium"
          >
            {tl.submit}
          </Link>
        </div>
      ) : (
        <div className="text-center space-y-3">
          <p className="text-sm text-ink/60 dark:text-paper/60">{tl.callbackBusy}</p>
          <Spinner />
        </div>
      )}
    </AuthShell>
  );
}

function Spinner() {
  return (
    <svg
      className="mx-auto h-6 w-6 animate-spin text-ink/40 dark:text-paper/40"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
